import { notifyLocationChanged, serverPath } from "@amtana/i18n";
import { useCallback, useSyncExternalStore } from "react";
import type { MouseEvent } from "react";

// Address-bar state lives in the router and nowhere else. This one is small on
// purpose: paths with params, one catch-all, query strings, history push/replace
// and in-page anchors.

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

function emit(): void {
  for (const listener of listeners) listener();
}

export function navigate(href: string, options: { replace?: boolean } = {}): void {
  const url = new URL(href, window.location.href);
  const samePath = url.pathname === window.location.pathname;
  if (options.replace) window.history.replaceState(null, "", url);
  else window.history.pushState(null, "", url);
  emit();
  // `pushState`/`replaceState` fire no event of their own; the website's "/es/" prefix (ADR-027
  // point 5) needs to be re-read the moment a navigation changes it.
  notifyLocationChanged();
  if (url.hash) document.getElementById(url.hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
  else if (!samePath) window.scrollTo({ top: 0 });
}

/** The current query string as an immutable map; changes re-render. */
export function useSearchParams(): URLSearchParams {
  const search = useSyncExternalStore(
    subscribe,
    () => window.location.search,
    () => "",
  );
  return new URLSearchParams(search);
}

/** Replaces one query parameter in place (filters, tabs, pages); undefined removes it. */
export function setSearchParam(name: string, value: string | undefined): void {
  const url = new URL(window.location.href);
  if (value === undefined || value === "") url.searchParams.delete(name);
  else url.searchParams.set(name, value);
  navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
}

/** Replaces several query parameters in one step (a table's filter and its page together), so
 * readers re-render once; undefined or empty removes one. */
export function setSearchParams(values: Readonly<Record<string, string | undefined>>): void {
  const url = new URL(window.location.href);
  for (const [name, value] of Object.entries(values)) {
    if (value === undefined || value === "") url.searchParams.delete(name);
    else url.searchParams.set(name, value);
  }
  navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
}

/** On the server there is no `window`; the render that is answering supplies the path instead
 * (`@amtana/i18n`'s `setServerPath`, called by `renderPage`). On the client, including the
 * hydration pass that must match what the server sent, the real URL is already there. */
function getServerSnapshot(): string {
  return typeof window === "undefined" ? serverPath() : window.location.pathname;
}

/**
 * Removes query parameters from the address bar in place, with no history entry and no notice to
 * `useSearchParams` (unlike `setSearchParam`, whose `navigate` re-renders every reader). Skipping
 * the notice does not keep the old parameters on screen: `useSearchParams` reads the address bar
 * again on every render, whatever triggers it, so the next one already sees them gone. A screen
 * keeps what it needs from its first render before calling this (the OIDC callback reads its
 * return once, in a lazy `useState`, #151). For a one-time credential no reload, back navigation
 * or restored tab may see or resend: the OIDC callback's `code` and `state` (D948), stripped
 * before the server is asked, so replaying the address bar alone can never replay the sign-in.
 */
export function discardOneTimeParams(names: readonly string[]): void {
  const url = new URL(window.location.href);
  for (const name of names) url.searchParams.delete(name);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

export function useLocationPath(): string {
  return useSyncExternalStore(subscribe, () => window.location.pathname, getServerSnapshot);
}

function isInApp(href: string): boolean {
  if (href.startsWith("#")) return true;
  const url = new URL(href, window.location.href);
  return url.origin === window.location.origin;
}

export type InAppLinkProps = {
  readonly href: string;
  readonly onClick: (event: MouseEvent<HTMLElement>) => void;
};

function followInApp(href: string, event: MouseEvent<HTMLElement>): void {
  if (!isInApp(href) || event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  navigate(href);
}

/**
 * Props for an in-app link: the href stays real (copyable, middle-clickable)
 * and a plain click navigates without reloading the document. For lists of
 * links, where a hook per item is not possible.
 */
export function inAppLink(href: string): InAppLinkProps {
  return { href, onClick: (event) => followInApp(href, event) };
}

/** The same props, stable across renders, for a single link in a component. */
export function useInAppLink(href: string): InAppLinkProps {
  const onClick = useCallback((event: MouseEvent<HTMLElement>) => followInApp(href, event), [href]);
  return { href, onClick };
}
